import { useCallback, useEffect, useRef } from 'react';
/**
 * A hook that handles focusable elements.
 *
 * @param skipBackToTopOnTab - checks if the back to top button should be skippable in tab navigation
 * @param targetId - the id of the element to resume tab navigation from
 *
 * @returns object with properties:
 *  - handleNextFocusableElement - If button is set to not be skipped on tab navigation and target element is found, it focuses to the target element so that the next focusable element would be in the desired place on the page
 */
const useFocusTarget = (
  skipBackToTopOnTab: boolean,
  targetId?: string
): { handleNextFocusableElement: () => void } => {
  const processedTargetId = useRef<string | undefined>();

  // Focuses programatically on the element if target element has been processed by the useEffect
  const handleNextFocusableElement = useCallback(() => {
    const targetElementId = processedTargetId.current;
    if (!targetElementId) {
      return;
    }

    const nextFocusableElement = document.querySelector(
      targetElementId.startsWith('#') ? targetElementId : `#${targetElementId}`
    );
    if (!nextFocusableElement) {
      return;
    }
    (nextFocusableElement as HTMLElement).focus();
  }, [processedTargetId]);

  //   If Back to Top should not be skipped on tab navigation and a target ID is set,
  // it checks if the element is present in the page.
  useEffect(() => {
    if (skipBackToTopOnTab || !targetId) {
      return;
    }

    const targetElement = document.querySelector(
      targetId.startsWith('#') ? targetId : `#${targetId}`
    );
    if (!targetElement || targetElement === null) {
      console.error(
        `Element with ID ${targetId} was not found, hence the tab order will remain the same after accessing the button.
        Make sure the element with the target ID is loaded on the page or set the Back to Top button to be skippable on tab navigation.`
      );
      return;
    }

    // Required for the tab navigation to work
    targetElement.setAttribute('tabindex', '-1');
    processedTargetId.current = targetId;
  }, [skipBackToTopOnTab, targetId]);

  return { handleNextFocusableElement };
};

export default useFocusTarget;
